import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Library as LibraryIcon, Moon, Download, Lock, Star, Check, FileText, Sparkles, AlertTriangle, ExternalLink, Loader2,
} from 'lucide-react';
import * as api from '../lib/api';
import { fmtSize } from '../lib/imageResize';
import { formatPrice } from '../lib/eventsFormat';
import LibraryCheckout from './LibraryCheckout';

const COVER_PALETTES = [
  ['#3A9A95', '#A99CC8'], ['#8A7BB3', '#4AADA8'], ['#6BC4BF', '#C5BBD9'], ['#4AADA8', '#8A7BB3'], ['#9fb7c9', '#C5BBD9'],
];
const hash = (str) => [...String(str)].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);

const TYPE_BY_EXTENSION = { pdf: 'PDF', zip: 'ZIP', docx: 'Word', xlsx: 'Excel', mp3: 'MP3', m4a: 'Audio', mp4: 'Video' };
const typeLabel = (item) => {
  if (item.kind === 'link') return item.link_url?.startsWith('/') ? 'Opens inside Sirati' : 'Opens in a new tab';
  const ext = (item.file_name || '').split('.').pop().toLowerCase();
  return [TYPE_BY_EXTENSION[ext] || 'File', item.file_size ? fmtSize(item.file_size) : null].filter(Boolean).join('  ·  ');
};

/** The cover you upload, or soft arches in the brand colours until you do. */
const Cover = ({ item, className = '' }) => {
  const [a, b] = COVER_PALETTES[hash(item.id) % COVER_PALETTES.length];
  return (
    <div className={`overflow-hidden ${className}`} style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}>
      {item.cover_url ? (
        <img src={item.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      ) : (
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <g fill="none" stroke="#fff" strokeWidth="3" opacity=".5">
            <path d="M40 300V170a70 70 0 0 1 140 0v130" /><path d="M110 300V140a90 90 0 0 1 180 0v160" /><path d="M230 300V180a62 62 0 0 1 124 0v120" />
          </g>
          <circle cx="318" cy="64" r="26" fill="#fff" opacity=".4" />
        </svg>
      )}
    </div>
  );
};

const Badge = ({ item }) => {
  const base = 'absolute left-3.5 top-3.5 z-[2] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-white/90 shadow-sm';
  if (item.status === 'coming_soon') return <span className={`${base} text-olive`}><Sparkles className="w-3.5 h-3.5" aria-hidden="true" />Coming soon</span>;
  if (item.access === 'free') return <span className={`${base} text-teal-dark`}>Free</span>;
  if (item.access === 'plus') return <span className={`${base} text-lavender-dark`}><Star className="w-3.5 h-3.5" aria-hidden="true" />Sirati+</span>;
  const included = item.reason === 'lifetime' || item.reason === 'admin';
  return <span className={`${base} text-teal-dark`}>{item.can_open ? (included ? 'Included' : 'Owned') : formatPrice(item.price_cents)}</span>;
};

const ResourceCard = ({ item, busy, done, error, onOpen, onBuy, onNavigate, index }) => {
  const featured = item.featured;
  const locked = !item.can_open && item.status !== 'coming_soon';
  const titleId = `lib-item-${item.id}`;

  let action;
  if (item.status === 'coming_soon') {
    action = <button disabled className="min-h-[46px] px-6 rounded-full border-[1.5px] border-teal/50 text-teal-dark font-bold inline-flex items-center gap-2 opacity-60"><Sparkles className="w-4 h-4" aria-hidden="true" />Coming soon</button>;
  } else if (item.can_open) {
    action = (
      <button
        onClick={() => onOpen(item)} disabled={busy}
        className={`min-h-[46px] px-6 rounded-full font-bold inline-flex items-center gap-2 text-white transition-transform active:scale-[0.98] disabled:opacity-70 ${done ? 'bg-green-600' : 'bg-primary btn-hover'}`}
        data-testid={`library-open-${item.id}`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : done ? <Check className="w-4 h-4" aria-hidden="true" /> : item.kind === 'link' ? <ExternalLink className="w-4 h-4" aria-hidden="true" /> : <Download className="w-4 h-4" aria-hidden="true" />}
        {busy ? 'Preparing...' : done ? 'Done. Get it again' : item.kind === 'link' ? 'Open' : 'Download'}
      </button>
    );
  } else if (item.reason === 'plus_only') {
    action = (
      <button onClick={() => onNavigate('premium')} className="min-h-[46px] px-6 rounded-full font-bold inline-flex items-center gap-2 text-white bg-gradient-to-br from-lavender-dark to-lavender transition-transform active:scale-[0.98]" data-testid={`library-unlock-${item.id}`}>
        <Star className="w-4 h-4" aria-hidden="true" />Unlock with Sirati+
      </button>
    );
  } else {
    action = (
      <button onClick={() => onBuy(item)} className="min-h-[46px] px-6 rounded-full font-bold text-white bg-primary btn-hover transition-transform active:scale-[0.98]" data-testid={`library-buy-${item.id}`}>
        Buy for {formatPrice(item.your_price_cents)}
      </button>
    );
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ type: 'spring', stiffness: 100, damping: 20, delay: index * 0.08 }}
      aria-labelledby={titleId}
      className={`glass-card rounded-[2rem] overflow-hidden flex flex-col shadow-[0_22px_40px_-28px_rgba(58,154,149,0.45)] hover:-translate-y-1 transition-transform duration-500 ${featured ? 'md:col-span-6 md:flex-row' : 'md:col-span-3'}`}
      data-testid={`library-item-${item.id}`}
    >
      <div className={`relative ${featured ? 'md:w-[42%] md:min-h-[300px] min-h-[210px]' : 'aspect-[16/9]'} ${item.status === 'coming_soon' ? 'saturate-50' : ''}`}>
        <Cover item={item} className="absolute inset-0" />
        {locked && <div className="absolute inset-0 bg-gradient-to-t from-[#0f1f3a]/55 to-[#0f1f3a]/10" aria-hidden="true" />}
        <Badge item={item} />
        {locked && <span className="absolute right-3.5 top-3.5 z-[2] w-[38px] h-[38px] rounded-full bg-white/90 flex items-center justify-center text-lavender-dark" aria-hidden="true"><Lock className="w-[17px] h-[17px]" /></span>}
      </div>
      <div className={`flex flex-col gap-2.5 flex-1 ${featured ? 'p-6 md:p-9 md:justify-center' : 'p-6'}`}>
        <h3 id={titleId} className={`font-heading font-semibold text-text-main leading-tight ${featured ? 'text-2xl md:text-[28px]' : 'text-xl'}`}>{item.title}</h3>
        {item.summary && <p className="text-[14.5px] leading-relaxed text-text-main/90 max-w-[56ch]">{item.summary}</p>}
        <p className="flex items-center gap-2 text-[12.5px] text-text-muted"><FileText className="w-3.5 h-3.5" aria-hidden="true" />{typeLabel(item)}{(item.reason === 'lifetime' && item.access === 'paid') && <b className="text-teal-dark"> · Included with Lifetime</b>}</p>
        {item.access === 'paid' && !item.can_open && item.status !== 'coming_soon' && (
          <p className="flex items-baseline gap-2">
            <span className="font-heading text-[22px] font-semibold text-text-main">{formatPrice(item.your_price_cents)}</span>
            {item.discount_pct > 0 && <><s className="text-sm text-text-muted">{formatPrice(item.price_cents)}</s><small className="text-xs font-bold text-teal-dark">{item.discount_pct}% off for Sirati+ members</small></>}
          </p>
        )}
        <div className="mt-auto pt-1.5">{action}</div>
        {error && <p role="alert" className="text-sm text-red-600" data-testid={`library-error-${item.id}`}>{error}</p>}
      </div>
    </motion.article>
  );
};

const Skeleton = () => (
  <>
    {[0, 1, 2].map((i) => (
      <div key={i} className={`relative overflow-hidden rounded-[2rem] glass-card min-h-[280px] ${i === 0 ? 'md:col-span-6 min-h-[300px]' : 'md:col-span-3'}`} aria-hidden="true">
        <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.4s_infinite] bg-gradient-to-r from-transparent via-white/50 to-transparent" />
      </div>
    ))}
  </>
);

const Message = ({ icon: Icon, title, text, action, tone }) => (
  <div className={`md:col-span-6 text-center py-14 px-6 rounded-[2rem] glass-card border border-dashed ${tone === 'error' ? 'border-red-400/60' : 'border-teal/50'}`} role={tone === 'error' ? 'alert' : undefined}>
    <span className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center ${tone === 'error' ? 'bg-red-100 text-red-600' : 'bg-teal/15 text-teal-dark'}`}><Icon className="w-6 h-6" aria-hidden="true" /></span>
    <h3 className="font-heading text-[22px] font-semibold text-text-main mt-3">{title}</h3>
    <p className="max-w-[46ch] mx-auto mt-1.5 mb-4 text-[14.5px] leading-relaxed text-text-main">{text}</p>
    {action}
  </div>
);

const CARD_BARS = [100, 62, 84, 48, 100, 70, 90, 55, 100, 76];
const CARD_CHIP = { free: 'Member', plus: 'Sirati+', lifetime: 'Lifetime', admin: 'Admin' };
const CARD_FALLBACK = {
  free: 'linear-gradient(135deg,#3A9A95 0%,#4AADA8 45%,#8A7BB3 100%)',
  lifetime: 'linear-gradient(135deg,#2f7f7b 0%,#4AADA8 30%,#C5BBD9 62%,#8A7BB3 100%)',
};

/** The member's Library card: name, join date and tier. Sirati+ and Lifetime can carry an admin-chosen picture. */
const LibraryCard = ({ tier, member, images }) => {
  const shiny = tier === 'lifetime' || tier === 'admin';
  const picture = tier === 'plus' ? images?.plus : shiny ? images?.lifetime : null;
  const name = member?.name || 'Member';
  const since = member?.since && !Number.isNaN(Date.parse(member.since))
    ? new Date(member.since).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : null;
  const background = picture ? `url("${picture}")` : CARD_FALLBACK[shiny ? 'lifetime' : 'free'];
  const shadow = 'inset 0 1px 0 rgba(255,255,255,.5),inset 0 0 0 1.5px rgba(255,255,255,.3),0 30px 44px -26px rgba(58,154,149,.55)';
  return (
    <div
      role="img" aria-label={`Sirati Library card for ${name}, ${CARD_CHIP[tier] || 'Member'}${since ? `, member since ${since}` : ''}`}
      className="relative aspect-[1.586/1] w-full max-w-[420px] rounded-[1.6rem] p-5 text-white overflow-hidden flex flex-col transition-transform duration-500 hover:-translate-y-[3px] hover:-rotate-[0.4deg]"
      style={{ background, backgroundSize: 'cover', backgroundPosition: 'center', boxShadow: shadow }}
      data-testid="library-card"
    >
      {picture ? (
        <span className="absolute inset-0 bg-gradient-to-br from-[#0f1f3a]/50 via-[#0f1f3a]/15 to-[#0f1f3a]/40" aria-hidden="true" />
      ) : (
        <>
          <span className="absolute -right-14 -top-16 w-[220px] h-[220px] rounded-full bg-white/10" aria-hidden="true" />
          <span className="absolute -left-10 -bottom-24 w-[240px] h-[240px] rounded-full bg-[#0f1f3a]/10" aria-hidden="true" />
        </>
      )}
      {shiny && <span className="library-card-sheen absolute inset-0 pointer-events-none" aria-hidden="true" />}
      <div className="relative flex items-center justify-between" aria-hidden="true">
        <span className="flex items-center gap-2 font-heading text-xs font-semibold tracking-[0.16em] uppercase"><Moon className="w-4 h-4" />Sirati</span>
        <span className="px-3 py-1 rounded-full text-[11.5px] font-bold bg-white/20 border border-white/35">{CARD_CHIP[tier] || 'Member'}</span>
      </div>
      <div className="relative mt-auto flex items-center gap-3.5" aria-hidden="true">
        {member?.avatar_url ? (
          <img src={member.avatar_url} alt="" className="w-[58px] h-[58px] rounded-full object-cover border-[2.5px] border-white/85 shrink-0" />
        ) : (
          <span className="w-[58px] h-[58px] rounded-full grid place-items-center font-heading text-2xl font-semibold border-[2.5px] border-white/85 shrink-0 bg-white/20">{name.charAt(0).toUpperCase()}</span>
        )}
        <div className="min-w-0">
          <div className="font-heading text-[22px] font-semibold leading-tight truncate drop-shadow-[0_1px_8px_rgba(10,22,40,.45)]">{name}</div>
          {since && <div className="text-xs opacity-90 mt-0.5 drop-shadow-[0_1px_8px_rgba(10,22,40,.45)]">Member since {since}</div>}
        </div>
      </div>
      <div className="relative mt-3.5 flex items-end justify-between" aria-hidden="true">
        <span className="font-heading text-[13px] font-medium tracking-wide">Sirati Library</span>
        <span className="flex gap-[2px] items-end h-[22px]">
          {CARD_BARS.map((h, i) => <i key={i} className="block w-[3px] rounded-t-sm bg-white/75" style={{ height: `${h}%` }} />)}
        </span>
      </div>
    </div>
  );
};

const TIER_COPY = {
  free: { chip: 'Member', rows: [['Free guides and tools', true], ['Sirati+ perks (unlock with Sirati+)', false], ['Paid resources at full price', true]] },
  plus: { chip: 'Sirati+ Member', rows: [['Free guides and tools', true], ['Every Sirati+ perk', true], ['Paid resources at 50% off', true]] },
  lifetime: { chip: 'Lifetime Member', rows: [['Free guides and tools', true], ['Every Sirati+ perk', true], ['Every paid resource, included', true]] },
  admin: { chip: 'Admin', rows: [['Everything in the Library', true]] },
};

const AccessPanel = ({ tier, onNavigate }) => {
  const copy = TIER_COPY[tier] || TIER_COPY.free;
  return (
    <section className="glass-card rounded-[1.6rem] p-5 text-[13.5px] leading-relaxed" aria-labelledby="library-access-title" data-testid="library-access">
      <h2 id="library-access-title" className="font-heading text-[15px] font-semibold text-text-main mb-2">Your access: {copy.chip}</h2>
      <ul className="space-y-2">
        {copy.rows.map(([text, on]) => (
          <li key={text} className={`flex gap-2.5 items-start ${on ? '' : 'text-text-muted'}`}>
            {on ? <Check className="w-4 h-4 mt-0.5 text-teal-dark shrink-0" aria-hidden="true" /> : <Lock className="w-4 h-4 mt-0.5 shrink-0 opacity-60" aria-hidden="true" />}
            <span>{text}</span>
          </li>
        ))}
      </ul>
      {tier === 'free' && (
        <button onClick={() => onNavigate('premium')} className="mt-4 min-h-[44px] px-5 rounded-full text-sm font-bold text-white bg-gradient-to-br from-lavender-dark to-lavender">See Sirati+</button>
      )}
    </section>
  );
};

/** The members-only Library (own page at /library). */
const LibraryPage = ({ onNavigate }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [busyId, setBusyId] = useState(null);
  const [done, setDone] = useState({});
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState('');
  const [buying, setBuying] = useState(null);
  const toastTimer = useRef(null);

  const load = useCallback(() => {
    setStatus('loading');
    api.getLibrary()
      .then((res) => { setData(res.data); setStatus('ok'); })
      .catch(() => setStatus('error'));
  }, []);
  useEffect(() => { load(); return () => clearTimeout(toastTimer.current); }, [load]);

  const showToast = (text) => {
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2600);
  };

  const open = async (item) => {
    setBusyId(item.id);
    setErrors((e) => ({ ...e, [item.id]: '' }));
    try {
      const res = await api.downloadLibraryItem(item.id);
      const { kind, url } = res.data;
      if (kind === 'link') {
        if (url.startsWith('/')) window.location.assign(url);
        else window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setDone((d) => ({ ...d, [item.id]: true }));
      showToast(kind === 'link' ? 'Opening now' : 'Your download is ready');
    } catch (err) {
      setErrors((e) => ({ ...e, [item.id]: err?.response?.data?.detail || 'We could not start that. Please try again.' }));
    } finally {
      setBusyId(null);
    }
  };

  const tier = data?.tier || 'free';
  const items = data?.items || [];

  return (
    <div className="max-w-[1240px] mx-auto px-4 sm:px-5 pt-4 pb-28" data-testid="library-page">
      <div className="relative h-[200px] md:h-[230px] rounded-[2rem] md:rounded-[2.5rem] overflow-hidden bg-gradient-to-br from-teal to-lavender shadow-[0_24px_50px_-28px_rgba(58,154,149,0.55)]">
        {data?.banner_url ? (
          <img src={data.banner_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <svg className="absolute inset-0 w-full h-full opacity-25" viewBox="0 0 1200 230" preserveAspectRatio="none" aria-hidden="true">
            <g fill="none" stroke="#fff" strokeWidth="2"><path d="M40 230V130a60 60 0 0 1 120 0v100M200 230V110a70 70 0 0 1 140 0v120M380 230V135a55 55 0 0 1 110 0v95M540 230V100a75 75 0 0 1 150 0v130M740 230V125a62 62 0 0 1 124 0v105M910 230V105a72 72 0 0 1 144 0v125M1090 230V140a50 50 0 0 1 100 0v90" /></g>
          </svg>
        )}
        <div className="absolute inset-x-0 bottom-0 h-[60%] bg-gradient-to-t from-[#0f1f3a]/45 to-transparent" aria-hidden="true" />
        <div className="absolute left-5 md:left-9 bottom-5 md:bottom-7 z-[2] text-white max-w-[520px] pr-4">
          <h1 className="font-heading text-[28px] md:text-4xl font-semibold flex items-center gap-3"><LibraryIcon className="w-7 h-7 md:w-8 md:h-8" aria-hidden="true" />Library</h1>
          <p className="mt-1.5 text-[15px] leading-relaxed">Guides, tools and resources for your journey to marriage readiness. Some are free for every member, some come with Sirati+</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-7 mt-8 items-start">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-5 md:gap-[22px]" aria-live="polite" aria-busy={status === 'loading'}>
          {status === 'loading' && <Skeleton />}
          {status === 'error' && (
            <Message icon={AlertTriangle} tone="error" title="We could not open the Library" text="Something went wrong on our side. Please try again in a moment."
              action={<button onClick={load} className="min-h-[46px] px-6 rounded-full bg-primary text-white font-bold btn-hover">Try again</button>} />
          )}
          {status === 'ok' && items.length === 0 && (
            <Message icon={LibraryIcon} title="The shelves are being stocked" text="New guides and tools are on their way. Check back soon." />
          )}
          {status === 'ok' && items.map((item, i) => (
            <ResourceCard key={item.id} item={item} index={i} busy={busyId === item.id} done={!!done[item.id]} error={errors[item.id]} onOpen={open} onBuy={setBuying} onNavigate={onNavigate} />
          ))}
        </div>
        <aside className="lg:sticky lg:top-24 grid gap-4" aria-label="Your Library access">
          {status === 'ok' && <LibraryCard tier={tier} member={data?.member} images={data?.card_images} />}
          {status === 'ok' && <AccessPanel tier={tier} onNavigate={onNavigate} />}
        </aside>
      </div>

      {buying && (
        <LibraryCheckout
          item={buying} onClose={() => setBuying(null)}
          onPurchased={load}
          onDownload={(it) => open(it)}
        />
      )}

      <div
        role="status" aria-live="polite"
        className={`fixed left-1/2 bottom-24 lg:bottom-7 -translate-x-1/2 z-[60] px-5 py-3 rounded-full bg-green-700 text-white font-semibold text-sm flex items-center gap-2.5 transition-all duration-500 ${toast ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6 pointer-events-none'}`}
        data-testid="library-toast"
      >
        {toast && <><Check className="w-4 h-4" aria-hidden="true" />{toast}</>}
      </div>
    </div>
  );
};

export default LibraryPage;
